import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import { Radio } from '@atlaskit/radio';
import { Checkbox } from '@atlaskit/checkbox';
import Select from '@atlaskit/select';
import { FieldHelpTooltip } from '../FieldHelpTooltip';
import {
  FormValues,
  ChampionPreference,
  YesNo,
  ChampionPicklistField,
} from '../../../types/ChampionApplicationFormTypes';
import { MAX_CHAMPION_ESSAY_LENGTH } from '../../../utils/ChampionApplicationFormValidation';

type FieldErrors = {
  howContributeOnline?: string;
  attendedAce?: string;
  aceExperience?: string;
  fourEventsPerYear?: string;
  activityAndEvent?: string;
  whyToCreateContent?: string;
  contentSharingFrequency?: string;
  bestContent1?: string;
  bestContent2?: string;
  bestContent3?: string;
  primarySocialProfile?: string;
  otherURL?: string;
  commitSharingContent?: string;
  trainingFrequency?: string;
  trainingSources?: string;
  activeCug?: string;
  eventsPerYear?: string;
  discussInCoE?: string;
  coeFocus?: string;
  otherExcellence?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
  trainingSourcesOptions: ChampionPicklistField['picklistValues'];
  trainingFrequencyOptions: ChampionPicklistField['picklistValues'];
  coeFocusOptions: ChampionPicklistField['picklistValues'];
  eventsPerYearOptions: ChampionPicklistField['picklistValues'];
  discussInCoEOptions: ChampionPicklistField['picklistValues'];
  activeCugOptions: ChampionPicklistField['picklistValues'];
  contentSharingFrequencyOptions: ChampionPicklistField['picklistValues'];
  fourEventsPerYearOptions: ChampionPicklistField['picklistValues'];
};

type SelectOption = { label: string; value: string };

export const PreferenceDetailsSection: React.FC<Props> = ({ values, updateField, errors, trainingSourcesOptions, trainingFrequencyOptions, coeFocusOptions, eventsPerYearOptions, discussInCoEOptions, activeCugOptions, contentSharingFrequencyOptions, fourEventsPerYearOptions }) => {
  const pref = values.championPreference;

  if (!pref) {
    return null;
  }

  // ---- Helpers -------------------------------------------------------------

  const renderYesNoRadios = (field: keyof FormValues, current: YesNo) => (
    <div css={controlTopStyles}>
      <Radio
        name={String(field)}
        label="Yes"
        value="yes"
        isChecked={current === 'yes'}
        onChange={() => updateField(field, 'yes' as any)}
      />
      <Radio
        name={String(field)}
        label="No"
        value="no"
        isChecked={current === 'no'}
        onChange={() => updateField(field, 'no' as any)}
      />
    </div>
  );



  const renderSelect = <K extends keyof FormValues>(opts: {
    id: string;
    label: string;
    field: K;
    value: FormValues[K] extends string | null ? FormValues[K] : never;
    options: SelectOption[];
    error?: string;
    required?: boolean;
    isInvalid?: boolean;
    boldLabel?: boolean;
  }) => {
    const { id, label, field, value, options, error, required, isInvalid, boldLabel } = opts;
    const currentValue = (value as string) ?? '';
    const selectOptions = options.filter((o) => o.value !== '');
    const selectedOption = currentValue
      ? selectOptions.find((o) => o.value === currentValue) ?? null
      : null;

    return (
      <div css={fieldStyles} className="champion-form-atlaskit-select">
        <label
          css={[labelStyles, salesforceLabelStyles, boldLabel && labelBoldStyles]}
          htmlFor={id}
        >
          {required && (
            <>
              <abbr css={requiredStyles} title="required" aria-hidden="true">
                *
              </abbr>{' '}
            </>
          )}
          {label}
        </label>
        <div css={selectControlWrapperStyles}>
          {/* TEMP: disable portal/positioning */}
          <Select<SelectOption, false>
            inputId={id}
            placeholder="Select an option"
            value={selectedOption}
            options={selectOptions}
            onChange={(opt) => updateField(field, (opt?.value ?? '') as FormValues[K])}
            isSearchable={false}
            styles={championFormSelectStyles}
            classNamePrefix="champion-form-select"
            aria-invalid={isInvalid}
          />
        </div>
        {error && <div css={errorStyles}>{error}</div>}
      </div>
    );
  };

  const toggleTrainingSource = (value: string) => {
    const current = values.trainingSources || [];
    const next = current.includes(value)
      ? current.filter((s) => s !== value)
      : [...current, value];
    updateField('trainingSources', next);
  };

  const renderTrainingSources = () => (
    <div
      css={fieldTopStyles}
      className={
        errors?.trainingSources
          ? 'champion-form-training-sources champion-form-training-sources--error'
          : 'champion-form-training-sources'
      }
    >
      <div css={fieldStyles}>
        <label css={[labelStyles, salesforceLabelStyles, labelBoldStyles]}>
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>{' '}
          What does your organization currently use for Atlassian training? Select all that apply.
        </label>

        <div css={controlTopStyles}>
          {trainingSourcesOptions.map((opt) => (
            <Checkbox
              key={opt.value}
              name="training_sources"
              label={opt.label}
              isChecked={values.trainingSources.includes(opt.value)}
              onChange={() => toggleTrainingSource(opt.value)}
            />
          ))}
        </div>

        {errors?.trainingSources && <div css={errorStyles}>{errors.trainingSources}</div>}
      </div>
    </div>
  );

  // ---- Conditional content by ChampionPreference ---------------------------

  switch (pref as ChampionPreference) {
    case 'Community Forums Contributor':
      return (
        <div css={sectionBoxStyles}>
          <h2 className="section-heading">Contributing to Atlassian Community Forums</h2>

          <details key={pref} css={detailsStyles}>
            <summary css={summaryStyles} className="champion-form-eligibility-summary">Am I eligible?</summary>
            <p css={detailsContentStyles}>
              To be eligible, you must be an active member of Atlassian Community Forums and have achieved{' '}
              <a
                href="https://community.atlassian.com/forums/Community-Announcements-articles/How-to-become-a-Rising-Star/ba-p/2130775"
                target="_blank"
                rel="noopener noreferrer"
              >
                Rising Star status
              </a>
              .
            </p>
          </details>

          <div css={fieldStyles}>
            <label css={[labelStyles, salesforceLabelStyles, textAreaLabelStyles]} htmlFor="how-contribute-online">
              <abbr css={requiredStyles} title="required" aria-hidden="true">
                *
              </abbr>{' '}
              How do you see yourself contributing to the Community Forums?
              <FieldHelpTooltip
                content="Please enter a minimum of 150 characters."
                iconLabel="How do you see yourself contributing Help Info"
              />
            </label>
            <div css={controlTopStyles}>
              <TextArea
                id="how-contribute-online"
                placeholder="Tell us in a few sentences - between 150 and 300 characters"
                value={values.howContributeOnline}
                onChange={(e) => updateField('howContributeOnline', e.target.value)}
                isInvalid={!!errors?.howContributeOnline}
                maxLength={MAX_CHAMPION_ESSAY_LENGTH}
              />
              {errors?.howContributeOnline && <div css={errorStyles}>{errors.howContributeOnline}</div>}
            </div>
          </div>
        </div>
      );

    case 'Community Event Organizer':
      return (
        <div css={sectionBoxStyles}>
          <h2 className="section-heading">Organizing Atlassian Community Events</h2>

          <details key={pref} css={detailsStyles}>
            <summary css={summaryStyles} className="champion-form-eligibility-summary">Am I eligible?</summary>
            <div css={detailsContentStyles}>
              <p>To be eligible, you must:</p>
              <ul css={listStyles}>
                <li>Have attended at least one Atlassian Community Event</li>
                <li>Commit to host at least one event per quarter (90 days), virtually or in person</li>
              </ul>
            </div>
          </details>

          <div
            css={fieldTopStyles}
            className={
              errors?.attendedAce
                ? 'champion-form-attended-ace champion-form-attended-ace--error'
                : 'champion-form-attended-ace'
            }
          >
            <label css={[labelStyles, salesforceLabelStyles, labelBoldStyles]}>
              <abbr css={requiredStyles} title="required" aria-hidden="true">
                *
              </abbr>{' '}
              Have you previously attended an Atlassian Community Event?
            </label>
            {renderYesNoRadios('attendedAce', values.attendedAce)}
            {errors?.attendedAce && <div css={errorStyles}>{errors.attendedAce}</div>}
          </div>

          {values.attendedAce === 'yes' && (
            <div css={fieldTopStyles}>
              <label css={[labelStyles, salesforceLabelStyles]} htmlFor="ace-experience">
                <abbr css={requiredStyles} title="required" aria-hidden="true">
                  *
                </abbr>{' '}
                Please tell us about your experience.
              </label>
              <TextArea
                id="ace-experience"
                value={values.aceExperience}
                onChange={(e) => updateField('aceExperience', e.target.value)}
                isInvalid={!!errors?.aceExperience}
                maxLength={32768}
              />
              {errors?.aceExperience && <div css={errorStyles}>{errors.aceExperience}</div>}
            </div>
          )}

          <div
            css={fieldTopStyles}
            className={
              errors?.fourEventsPerYear
                ? 'champion-form-four-events champion-form-four-events--error'
                : 'champion-form-four-events'
            }
          >
            {renderSelect({
              id: 'four-events-per-year',
              label: 'Can you commit to host at least one event every 90 days (virtually or in person)?',
              field: 'fourEventsPerYear',
              value: values.fourEventsPerYear,
              options: [
                { value: '', label: 'Select one' },
                ...fourEventsPerYearOptions.map((o) => ({ value: o.value, label: o.label })),
              ],
              error: errors?.fourEventsPerYear,
              required: true,
              isInvalid: !!errors?.fourEventsPerYear,
            })}
          </div>

          <div css={fieldTopStyles} className="champion-form-activity-and-event">
            <label css={[labelStyles, salesforceLabelStyles]} htmlFor="activity-and-event">
              <abbr css={requiredStyles} title="required" aria-hidden="true">
                *
              </abbr>{' '}
              What kind of activities and events are you hoping to organize?
            </label>
            <div css={controlTopStyles}>
              <Textfield
                id="activity-and-event"
                value={values.activityAndEvent}
                onChange={(e) => updateField('activityAndEvent', (e.target as HTMLInputElement).value)}
                isInvalid={!!errors?.activityAndEvent}
                isCompact
                maxLength={255}
              />
              {errors?.activityAndEvent && <div css={errorStyles}>{errors.activityAndEvent}</div>}
            </div>
          </div>
        </div>
      );

    case 'Creator':
      return (
        <div css={sectionBoxStyles}>
          <h2 className="section-heading">Creating thought leadership or learning content</h2>

          <details key={pref} css={detailsStyles}>
            <summary css={summaryStyles} className="champion-form-eligibility-summary">Am I eligible?</summary>
            <p css={detailsContentStyles}>
              To be eligible, you need to have shared at least two pieces of original Atlassian-focused thought
              leadership or learning content on social media each month in the last 90 days.
            </p>
          </details>

          <div css={fieldStyles}>
            <label css={[labelStyles, salesforceLabelStyles, textAreaLabelStyles]} htmlFor="why-create-content">
              <abbr css={requiredStyles} title="required" aria-hidden="true">
                *
              </abbr>{' '}
              Why do you create Atlassian-focused content on social media?
            </label>
            <div css={controlTopStyles}>
              <TextArea
                id="why-create-content"
                placeholder="Tell us in a few sentences - between 150 and 300 characters"
                value={values.whyToCreateContent}
                onChange={(e) => updateField('whyToCreateContent', e.target.value)}
                isInvalid={!!errors?.whyToCreateContent}
                maxLength={MAX_CHAMPION_ESSAY_LENGTH}
              />
              {errors?.whyToCreateContent && <div css={errorStyles}>{errors.whyToCreateContent}</div>}
            </div>
          </div>

          <div
            css={fieldTopStyles}
            className={
              errors?.contentSharingFrequency
                ? 'champion-form-content-sharing champion-form-content-sharing--error'
                : 'champion-form-content-sharing'
            }
          >
            {renderSelect({
              id: 'content-sharing-frequency',
              label: 'How often do you currently share your own content about Atlassian on social media?',
              field: 'contentSharingFrequency',
              value: values.contentSharingFrequency,
              options: [
                { value: '', label: 'Select one' },
                ...contentSharingFrequencyOptions.map((o) => ({ value: o.value, label: o.label })),
              ],
              error: errors?.contentSharingFrequency,
              required: true,
              isInvalid: !!errors?.contentSharingFrequency,
            })}
          </div>

          <div css={fieldTopStyles}>
            <div css={sectionHeadingStyles}>
              Share three examples of original content you&rsquo;ve created about Atlassian in the last 90 days.
            </div>
            <div css={fieldSmallTopStyles}>
              <label css={[labelStyles, salesforceLabelStyles]} htmlFor="best-content-1">
                <abbr css={requiredStyles} title="required" aria-hidden="true">
                  *
                </abbr>{' '}
                Example 1
              </label>
              <Textfield
                id="best-content-1"
                value={values.bestContent1}
                onChange={(e) => updateField('bestContent1', (e.target as HTMLInputElement).value)}
                isCompact
                isInvalid={!!errors?.bestContent1}
                maxLength={255}
              />
              {errors?.bestContent1 && <div css={errorStyles}>{errors.bestContent1}</div>}
            </div>
            <div css={fieldSmallTopStyles}>
              <label css={[labelStyles, salesforceLabelStyles]} htmlFor="best-content-2">
                <abbr css={requiredStyles} title="required" aria-hidden="true">
                  *
                </abbr>{' '}
                Example 2
              </label>
              <Textfield
                id="best-content-2"
                value={values.bestContent2}
                onChange={(e) => updateField('bestContent2', (e.target as HTMLInputElement).value)}
                isCompact
                isInvalid={!!errors?.bestContent2}
                maxLength={255}
              />
              {errors?.bestContent2 && <div css={errorStyles}>{errors.bestContent2}</div>}
            </div>
            <div css={fieldSmallTopStyles}>
              <label css={[labelStyles, salesforceLabelStyles]} htmlFor="best-content-3">
                <abbr css={requiredStyles} title="required" aria-hidden="true">
                  *
                </abbr>{' '}
                Example 3
              </label>
              <Textfield
                id="best-content-3"
                value={values.bestContent3}
                onChange={(e) => updateField('bestContent3', (e.target as HTMLInputElement).value)}
                isCompact
                isInvalid={!!errors?.bestContent3}
                maxLength={255}
              />
              {errors?.bestContent3 && <div css={errorStyles}>{errors.bestContent3}</div>}
            </div>
          </div>

          <div css={fieldTopStyles}>
            <div css={fieldStyles}>
              <label css={[labelStyles, salesforceLabelStyles]} htmlFor="primary-social-profile">
                <abbr css={requiredStyles} title="required" aria-hidden="true">
                  *
                </abbr>{' '}
                Primary social media profile URL
              </label>
              <Textfield
                id="primary-social-profile"
                placeholder="For example, https://www.youtube.com/@username"
                value={values.primarySocialProfile}
                onChange={(e) => updateField('primarySocialProfile', (e.target as HTMLInputElement).value)}
                isCompact
                isInvalid={!!errors?.primarySocialProfile}
                maxLength={255}
              />
              {errors?.primarySocialProfile && <div css={errorStyles}>{errors.primarySocialProfile}</div>}
            </div>
            <div css={fieldSmallTopStyles}>
              <label css={[labelStyles, salesforceLabelStyles]} htmlFor="other-urls">
                Other social media profiles
                <FieldHelpTooltip
                  content="Add any additional social media profiles you'd like us to see."
                  iconLabel="Other social media profiles Help Info"
                />
              </label>
              <Textfield
                id="other-urls"
                placeholder="Enter the URL for any other social media profile you want to share"
                value={values.otherURL}
                onChange={(e) => updateField('otherURL', (e.target as HTMLInputElement).value)}
                isCompact
                isInvalid={!!errors?.otherURL}
                maxLength={255}
              />
              {errors?.otherURL && <div css={errorStyles}>{errors.otherURL}</div>}
            </div>
          </div>

          <div
            css={fieldTopStyles}
            className={
              errors?.commitSharingContent
                ? 'champion-form-commit-sharing champion-form-commit-sharing--error'
                : 'champion-form-commit-sharing'
            }
          >
            <label css={[labelStyles, salesforceLabelStyles, labelBoldStyles]}>
              <abbr css={requiredStyles} title="required" aria-hidden="true">
                *
              </abbr>{' '}
              Can you commit to sharing 2 pieces of content per month?
            </label>
            {renderYesNoRadios('commitSharingContent', values.commitSharingContent)}
            {errors?.commitSharingContent && <div css={errorStyles}>{errors.commitSharingContent}</div>}
          </div>
        </div>
      );

    case 'Trainer/Mentor':
      return (
        <div css={sectionBoxStyles}>
          <h2 className="section-heading">Creating or delivering Atlassian training for end users</h2>

          <div
            css={fieldTopStyles}
            className={
              errors?.trainingFrequency
                ? 'champion-form-training-frequency champion-form-training-frequency--error'
                : 'champion-form-training-frequency'
            }
          >
            {renderSelect({
              id: 'training-frequency',
              label: 'How often do you build training on Atlassian for end users?',
              field: 'trainingFrequency',
              value: values.trainingFrequency,
              options: [
                { value: '', label: 'Select one' },
                ...trainingFrequencyOptions.map((o) => ({ value: o.value, label: o.label })),
              ],
              error: errors?.trainingFrequency,
              required: true,
              isInvalid: !!errors?.trainingFrequency,
            })}
          </div>

          {renderTrainingSources()}
        </div>
      );

    case 'CUG Leader':
      return (
        <div css={sectionBoxStyles}>
          <h2 className="section-heading">Running an Atlassian-focused Company User Group</h2>

          <div
            css={fieldTopStyles}
            className={
              errors?.activeCug
                ? 'champion-form-active-cug champion-form-active-cug--error'
                : 'champion-form-active-cug'
            }
          >
            <label css={[labelStyles, salesforceLabelStyles, labelBoldStyles]}>
              <abbr css={requiredStyles} title="required" aria-hidden="true">
                *
              </abbr>{' '}
              Do you currently have an active Atlassian focused Company User Group in your organization?
            </label>
            <div css={controlTopStyles}>
              {activeCugOptions.map((opt) => (
                <Radio
                  key={opt.value}
                  name="activeCug"
                  label={opt.label}
                  value={opt.value}
                  isChecked={values.activeCug === opt.value}
                  onChange={() => updateField('activeCug', opt.value)}
                />
              ))}
            </div>
            {errors?.activeCug && <div css={errorStyles}>{errors.activeCug}</div>}
          </div>

          <div
            css={fieldTopStyles}
            className={
              errors?.eventsPerYear
                ? 'champion-form-events-per-year champion-form-events-per-year--error'
                : 'champion-form-events-per-year'
            }
          >
            {renderSelect({
              id: 'events-per-year',
              label: 'How many Company User Group sessions or other training events do you run each year?',
              field: 'eventsPerYear',
              value: values.eventsPerYear,
              options: [
                { value: '', label: 'Select one' },
                ...eventsPerYearOptions.map((o) => ({ value: o.value, label: o.label })),
              ],
              error: errors?.eventsPerYear,
              required: true,
              isInvalid: !!errors?.eventsPerYear,
            })}
          </div>
        </div>
      );

    case 'CoE Member':
      return (
        <div css={sectionBoxStyles}>
          <h2 className="section-heading">Contributing to a Center of Excellence on Atlassian topics</h2>

          <div
            css={fieldTopStyles}
            className={
              errors?.discussInCoE
                ? 'champion-form-discuss-coe champion-form-discuss-coe--error'
                : 'champion-form-discuss-coe'
            }
          >
            <label css={[labelStyles, salesforceLabelStyles, labelBoldStyles]}>
              <abbr css={requiredStyles} title="required" aria-hidden="true">
                *
              </abbr>{' '}
              Do you currently discuss Atlassian in a Center of Excellence?
            </label>
            <div css={controlTopStyles}>
              {discussInCoEOptions.map((opt) => (
                <Radio
                  key={opt.value}
                  name="discussInCoE"
                  label={opt.label}
                  value={opt.value}
                  isChecked={values.discussInCoE === opt.value}
                  onChange={() => updateField('discussInCoE', opt.value)}
                />
              ))}
            </div>
            {errors?.discussInCoE && <div css={errorStyles}>{errors.discussInCoE}</div>}
          </div>

          <div
            css={[fieldStyles, fieldTopStyles]}
            className={
              errors?.coeFocus
                ? 'champion-form-coe-focus champion-form-coe-focus--error'
                : 'champion-form-coe-focus'
            }
          >
            <label css={[labelStyles, salesforceLabelStyles, labelBoldStyles]}>
              <abbr css={requiredStyles} title="required" aria-hidden="true">
                *
              </abbr>{' '}
              What&apos;s the focus of your Center of Excellence?
            </label>

            <div css={controlTopStyles}>
              {coeFocusOptions.map((opt) => (
                <Radio
                  key={opt.value}
                  name="coe_focus"
                  label={opt.label}
                  value={opt.value}
                  isChecked={values.coeFocus === opt.value}
                  onChange={() => updateField('coeFocus', opt.value)}
                />
              ))}
            </div>

            {errors?.coeFocus && <div css={errorStyles}>{errors.coeFocus}</div>}
          </div>

          {values.coeFocus === 'Other' && (
            <div css={[fieldStyles, fieldTopStyles]} className="champion-form-coe-focus-other">
              {/* Other text box — styled same as First name via .champion-form [data-ds--text-field--container] */}
              <label css={[labelStyles, salesforceLabelStyles]} htmlFor="other-excellence">
                <abbr css={requiredStyles} title="required" aria-hidden="true">
                  *
                </abbr>{' '}
                Other
              </label>
              <Textfield
                id="other-excellence"
                value={values.otherExcellence}
                maxLength={100}
                onChange={(e) => updateField('otherExcellence', (e.target as HTMLInputElement).value)}
                isCompact
                isInvalid={!!errors?.otherExcellence}
              />
              {errors?.otherExcellence && <div css={errorStyles}>{errors.otherExcellence}</div>}
            </div>
          )}
        </div>
      );

    default:
      return null;
  }
};

const sectionBoxStyles = css({
  marginBottom: '40px',
});

/* Section heading: matches First name label (0.75rem, #696969), no asterisk */
const sectionHeadingStyles = css({
  fontWeight: 400,
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  color: '#696969',
  marginBottom: token('space.050'),
  lineHeight: 1.5,
});

const detailsStyles = css({
  marginTop: token('space.050'),
  marginBottom: '1rem',
  maxWidth: '600px',
  border: '1px solid transparent',
  borderRadius: '0.25rem',
  padding: '0.5rem 0.75rem',
  transition: 'border-color 0.2s',
  /* Content always matches First name color */
  '& summary ~ *': {
    color: '#696969',
  },
  /* Blue when focused (clicked); disappears when click elsewhere */
  '&:focus-within': {
    borderColor: 'var(--champion-form-blue)',
  },
  '&:focus-within summary': {
    color: 'var(--champion-form-blue)',
  },
  '&[open] summary::before': {
    transform: 'rotate(0deg)', /* down */
  },
});

const summaryStyles = css({
  cursor: 'pointer',
  color: '#696969',
  fontSize: '10pt',
  fontFamily: 'sans-serif',
  fontWeight: 500,
  listStyle: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.25rem 0',
  transition: 'color 0.2s',
  '&::-webkit-details-marker': { display: 'none' },
  outline: 'none',
  '::before': {
    content: '""',
    display: 'inline-block',
    flexShrink: 0,
    width: '1rem',
    height: '1rem',
    backgroundColor: 'currentColor',
    maskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 520 520'%3E%3Cpath fill='black' d='M476 178L271 385c-6 6-16 6-22 0L44 178c-6-6-6-16 0-22l22-22c6-6 16-6 22 0l161 163c6 6 16 6 22 0l161-163c6-6 16-6 22 0l22 22c5 7 5 16 0 22'/%3E%3C/svg%3E")`,
    WebkitMaskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 520 520'%3E%3Cpath fill='black' d='M476 178L271 385c-6 6-16 6-22 0L44 178c-6-6-6-16 0-22l22-22c6-6 16-6 22 0l161 163c6 6 16 6 22 0l161-163c6-6 16-6 22 0l22 22c5 7 5 16 0 22'/%3E%3C/svg%3E")`,
    maskSize: 'contain',
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    transform: 'rotate(-90deg)', /* right when closed */
    transition: 'transform 0.2s',
  },
  ':hover': {
    color: 'var(--champion-form-blue)',
  },
  ':focus': {
    color: 'var(--champion-form-blue)',
    outline: 'none',
  },
});

const detailsContentStyles = css({
  marginTop: token('space.050'),
  fontSize: '10pt',
  fontFamily: 'sans-serif',
  color: '#696969',
  lineHeight: 1.5,
});

const fieldStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

/* Atlaskit Select wrapper — margin for label spacing */
const selectControlWrapperStyles = css({
  marginTop: token('space.050'),
});

/* Champion form Atlaskit Select — radio button colors for boundary & selection */
const CHAMPION_BLUE = 'rgb(7, 71, 166)';
const CHAMPION_BLUE_HOVER = 'rgb(5, 57, 133)';
const CHAMPION_BLUE_LIGHT = '#E9F2FF';

const championFormSelectStyles = {
  container: (base: object) => ({
    ...base,
    width: '100%',
  }),
  control: (base: object, state: { isFocused: boolean }) => ({
    ...base,
    minHeight: '1.875rem',
    height: '1.875rem',
    borderColor: state.isFocused ? CHAMPION_BLUE : '#757575',
    borderWidth: state.isFocused ? '1.5px' : '1px',
    borderRadius: '0.25rem',
    backgroundColor: '#fff',
    boxShadow: state.isFocused ? `0 0 0 1px ${CHAMPION_BLUE}` : 'none',
    flexWrap: 'nowrap',
    overflow: 'visible',
  }),
  menu: (base: object) => ({
    ...base,
    backgroundColor: '#fff',
    zIndex: 9999,
    boxShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 4px 11px rgba(0,0,0,0.1)',
    borderRadius: '0.25rem',
    display: 'block',
    visibility: 'visible',
  }),
  menuPortal: (base: object) => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: (base: object) => ({
    ...base,
    backgroundColor: '#fff',
    padding: '4px 0',
    maxHeight: 300,
    overflowY: 'auto',
  }),
  option: (base: object, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...base,
    fontSize: '0.8125rem',
    color: state.isSelected ? CHAMPION_BLUE_HOVER : '#2e2e2e',
    backgroundColor: state.isSelected
      ? CHAMPION_BLUE_LIGHT
      : state.isFocused
        ? '#f3f2f2'
        : '#fff',
    padding: '0.5rem 0.75rem',
    cursor: 'pointer',
  }),
  singleValue: (base: object) => ({
    ...base,
    fontSize: '0.8125rem',
    color: '#2e2e2e',
  }),
  placeholder: (base: object) => ({
    ...base,
    fontSize: '0.8125rem',
    color: '#706e6b',
  }),
  indicatorsContainer: (base: object) => ({
    ...base,
    paddingRight: '4px',
    display: 'flex',
    alignItems: 'center',
    overflow: 'visible',
    flexShrink: 0,
  }),
  dropdownIndicator: (base: object) => ({
    ...base,
    padding: '2px 4px',
    color: '#757575',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
};

const fieldTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '1rem',
});

const fieldSmallTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: token('space.050'),
});

const controlTopStyles = css({
  marginTop: token('space.050'),
});

const labelStyles = css({
  fontWeight: 400,
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  color: '#696969',
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.5,
});

const salesforceLabelStyles = css({
  color: '#696969',
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  fontWeight: 400,
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
  lineHeight: 1.5,
});

/* Text area label: 0.8125rem, not bold (matches Motivation field) */
const textAreaLabelStyles = css({
  fontSize: '0.8125rem',
  fontWeight: 400,
});

const requiredStyles = css({
  color: 'var(--champion-form-error)',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const labelBoldStyles = css({
  fontWeight: 600,
});

const listStyles = css({
  listStyleType: 'disc',
  paddingLeft: token('space.300'),
  marginTop: token('space.050'),
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});
